/**
 * CompareDeals -- side-by-side comparison of 2-4 marketplace listings, with
 * an on-demand multi-persona AI analyst review (LPM-1 forward work: the
 * compare-deals iteration of the listing marketplace UX).
 *
 * Deals arrive via the ?ids= query param (set by the Marketplace compare
 * bar) and are resolved through POST /marketplace/compare -- the SAME
 * masking + eligibility path as the browse list, so this page can never
 * show more counterparty identity than the marketplace itself.
 *
 * The analyst panel is opt-in per visit (a button, not an automatic fetch).
 * Run analysis submits the selected deals as one run on the Aaim Conductor
 * covering three personas (credit risk, regulatory compliance, portfolio
 * strategy); POST .../compare/analyze answers 202 with the run at QUEUED and
 * the page polls GET .../compare/analyses/{runId} until the run is COMPLETED
 * or FAILED (GH #1968). Each result names the persona, workflow or panel the
 * Conductor ran for that lens; no model provider or model id reaches this
 * page. A failed result shows its closed-set reason, never fabricated output,
 * and a 503 means the analyst service took no run. Every rendered opinion
 * carries the decision-support disclaimer.
 */

import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Bot, Inbox } from 'lucide-react';
import {
  BackLink,
  Badge,
  Button,
  Card,
  EmptyState,
  Skeleton,
  TableScroll,
} from '@aaimplatform/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { components as LspComponents } from '@aaimplatform/institutional-types/loan-syndication-protocol';
import { loanSyndicationClient } from '../../api/client';
import { RouterLinkComponent } from '../../lib/router-link';
import { ELIGIBILITY_BADGE_VARIANT, ELIGIBILITY_LABEL } from './eligibility';
import {
  formatPercent,
  formatRatioPercent,
  formatCurrencyGrid as formatCurrencyFull,
  formatDate,
  formatLeadDisplay,
  formatAssetType,
  formatCommitted,
  isLeadMasked,
} from './format';

type ParticipationOffering = LspComponents['schemas']['ParticipationOffering'];
type DealAnalysisResult = LspComponents['schemas']['DealAnalysisResult'];
type DealAnalysisPersona = LspComponents['schemas']['DealAnalysisPersona'];
type DealAnalysisPersonaResult = LspComponents['schemas']['DealAnalysisPersonaResult'];
type DealAnalysisAnalystKind = LspComponents['schemas']['DealAnalysisAnalystKind'];
type DealAnalysisFailureReason = LspComponents['schemas']['DealAnalysisFailureReason'];

// ---------------------------------------------------------------------------
// Formatters (mirror Marketplace.tsx conventions)
// ---------------------------------------------------------------------------

const REGULATORY_FRAMEWORK_LABELS: Record<string, string> = {
  NCUA_701_22: 'NCUA 701.22',
  OCC_12_CFR_32: 'OCC 12 CFR 32',
  FDIC: 'FDIC',
  STATE: 'State-chartered',
  OTHER: 'Other',
};

const PERSONA_LABELS: Record<DealAnalysisPersona, { title: string; focus: string }> = {
  CREDIT_RISK: {
    title: 'Credit Risk',
    focus: 'Collateral quality, LTV, concentration, loan structure',
  },
  REGULATORY_COMPLIANCE: {
    title: 'Regulatory Compliance',
    focus: 'Framework fit, eligibility reasons, retention considerations',
  },
  PORTFOLIO_STRATEGY: {
    title: 'Portfolio Strategy',
    focus: 'Yield attractiveness, diversification, deal structure',
  },
};

const ANALYST_KIND_LABELS: Record<DealAnalysisAnalystKind, string> = {
  PERSONA: 'Analyst',
  WORKFLOW: 'Workflow',
  PANEL: 'Panel',
};

const FAILURE_REASON_LABELS: Record<DealAnalysisFailureReason, string> = {
  ANALYST_UNAVAILABLE: 'The analyst could not be run for this lens.',
  TIMED_OUT: 'The analyst did not finish within the time allowed.',
  UNPARSEABLE_REPORT: 'The analyst finished, but its report could not be read.',
};

/** Poll cadence for a submitted run while it is QUEUED or RUNNING. */
const ANALYSIS_POLL_INTERVAL_MS = 3_000;

function isRunSettled(run: DealAnalysisResult | undefined): boolean {
  return run?.status === 'COMPLETED' || run?.status === 'FAILED';
}

// ---------------------------------------------------------------------------
// Data hooks
// ---------------------------------------------------------------------------

function useComparison(offeringIds: string[]) {
  return useQuery({
    queryKey: ['lsp-compare', offeringIds.join(',')],
    queryFn: async () => {
      const { data, error } = await loanSyndicationClient.POST(
        '/api/v1/syndication/marketplace/compare',
        { body: { offeringIds } },
      );
      if (error) {
        throw new Error((error as { message?: string })?.message ?? 'Failed to load the comparison');
      }
      return data;
    },
    enabled: offeringIds.length >= 2 && offeringIds.length <= 4,
    staleTime: 30 * 1000,
    retry: false,
  });
}

function useSubmitAnalysis(offeringIds: string[]) {
  return useMutation({
    mutationFn: async (): Promise<DealAnalysisResult> => {
      const { data, error, response } = await loanSyndicationClient.POST(
        '/api/v1/syndication/marketplace/compare/analyze',
        { body: { offeringIds } },
      );
      if (response.status === 503) {
        throw new Error('The analyst service is unavailable. Nothing was submitted; try again later.');
      }
      if (error || !data) {
        throw new Error(error?.message ?? 'The analysis could not be submitted');
      }
      return data;
    },
  });
}

/** Reads a submitted run, polling until it settles. */
function useAnalysisRun(runId: string | undefined) {
  return useQuery({
    queryKey: ['lsp-compare-analysis', runId],
    queryFn: async (): Promise<DealAnalysisResult> => {
      if (!runId) throw new Error('runId is required');
      const { data, error } = await loanSyndicationClient.GET(
        '/api/v1/syndication/marketplace/compare/analyses/{runId}',
        { params: { path: { runId } } },
      );
      if (error || !data) {
        throw new Error(error?.message ?? 'Failed to load the analysis');
      }
      return data;
    },
    enabled: !!runId,
    refetchInterval: (query) => (isRunSettled(query.state.data) ? false : ANALYSIS_POLL_INTERVAL_MS),
    refetchOnWindowFocus: false,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Comparison matrix -- attributes as rows, deals as columns
// ---------------------------------------------------------------------------

function buildMatrixRows(): Array<{ label: string; render: (o: ParticipationOffering) => React.ReactNode }> {
  const mono = (content: React.ReactNode) => (
    <span className="text-sm font-sans font-medium text-primary tabular-nums">{content}</span>
  );
  return [
    {
      label: 'Eligibility',
      render: (o) => (o.eligibility ? (
        <Badge variant={ELIGIBILITY_BADGE_VARIANT[o.eligibility.verdict]} size="sm">
          {ELIGIBILITY_LABEL[o.eligibility.verdict]}
        </Badge>
      ) : (
        <span className="text-xs font-sans text-tertiary">--</span>
      )),
    },
    {
      label: 'Framework',
      render: (o) => (
        <Badge variant="neutral" size="sm">
          {REGULATORY_FRAMEWORK_LABELS[o.regulatoryFramework ?? 'OTHER']}
        </Badge>
      ),
    },
    { label: 'Status', render: (o) => <Badge variant="neutral" size="sm">{o.status}</Badge> },
    { label: 'Loan amount', render: (o) => mono(formatCurrencyFull(o.loanDetails.loanAmount)) },
    {
      label: 'Rate',
      render: (o) => mono(
        <>
          {formatPercent(o.loanDetails.interestRate)}
          <span className="ml-1 text-xs text-tertiary">
            {o.loanDetails.rateType === 'VARIABLE' ? 'var' : 'fix'}
          </span>
        </>,
      ),
    },
    { label: 'Term', render: (o) => mono(`${o.loanDetails.termMonths} mo`) },
    { label: 'Asset type', render: (o) => <span className="text-sm font-sans text-secondary">{formatAssetType(o)}</span> },
    { label: 'LTV', render: (o) => mono(formatRatioPercent(o.collateralSummary?.ltvRatio)) },
    { label: 'Collateral value', render: (o) => mono(formatCurrencyFull(o.collateralSummary?.totalValue)) },
    { label: 'Syndication total', render: (o) => mono(formatCurrencyFull(o.participationStructure.totalSyndicationAmount)) },
    { label: 'Lead retained', render: (o) => mono(formatCurrencyFull(o.participationStructure.leadRetainedAmount)) },
    { label: 'Remaining capacity', render: (o) => mono(formatCurrencyFull(o.remainingCapacity)) },
    { label: 'Minimum commitment', render: (o) => mono(formatCurrencyFull(o.participationStructure.minimumCommitment)) },
    {
      // Distinct participants or commitment rows, each in its own unit, the
      // same line the marketplace grid prints; an absent count is not a 0
      // (r3 fi-g9-6).
      label: 'Committed',
      render: (o) => mono(formatCommitted(o) ?? 'Not reported'),
    },
    { label: 'Distribution', render: (o) => <span className="text-sm font-sans text-secondary">{o.participationStructure.distributionWaterfall === 'PRO_RATA' ? 'Pro rata' : 'Sequential'}</span> },
    { label: 'Commitment deadline', render: (o) => <span className="text-sm font-sans text-secondary">{formatDate(o.commitmentDeadline)}</span> },
  ];
}

function ComparisonMatrix({ offerings }: { offerings: ParticipationOffering[] }) {
  const rows = useMemo(buildMatrixRows, []);
  return (
    <Card className="p-0">
      <TableScroll label="Deal comparison">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-elevated/50">
              <th className="px-4 py-3 text-left text-label-xs font-sans font-semibold uppercase tracking-widest text-tertiary w-48">
                Deal terms
              </th>
              {offerings.map((o) => (
                // normal-case + tracking-normal: the tailwind-preset base layer
                // uppercases every bare th (base.css th rule); deal titles are
                // proper names and keep their authored case.
                <th key={o.offeringId} className="px-4 py-3 text-left normal-case tracking-normal">
                  <div className="text-sm font-sans font-medium text-primary leading-normal">
                    {o.dealName ?? formatLeadDisplay(o)}
                  </div>
                  <div className="text-xs font-sans text-tertiary leading-normal">
                    {o.dealName
                      ? formatLeadDisplay(o)
                      : (isLeadMasked(o) ? 'Counterparties masked' : null)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-t border-recessed even:bg-canvas/60">
                <td className="px-4 py-2.5 text-xs font-sans font-semibold uppercase tracking-wide text-tertiary">
                  {row.label}
                </td>
                {offerings.map((o) => (
                  <td key={o.offeringId} className="px-4 py-2.5">
                    {row.render(o)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// AI analyst panel
// ---------------------------------------------------------------------------

function AnalystResultCard({ result }: { result: DealAnalysisPersonaResult }) {
  const meta = PERSONA_LABELS[result.persona];
  return (
    <Card className="p-4">
      <div className="mb-3">
        <div className="text-sm font-sans font-semibold text-primary">{meta.title}</div>
        <div className="text-xs font-sans text-tertiary leading-normal">{meta.focus}</div>
      </div>
      <div className="rounded-sm border border-recessed p-4">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className="text-sm font-sans font-medium text-primary">{result.analyst.displayName}</span>
          <Badge variant="neutral" size="sm">{ANALYST_KIND_LABELS[result.analyst.kind]}</Badge>
        </div>
        {result.analyst.description && (
          <p className="text-xs font-sans text-tertiary leading-normal mb-2">{result.analyst.description}</p>
        )}
        {result.status === 'FAILED' ? (
          <div className="flex items-center gap-2">
            <Badge variant="error" size="sm">Failed</Badge>
            <span className="text-xs font-sans text-tertiary leading-normal">
              {result.failureReason
                ? FAILURE_REASON_LABELS[result.failureReason]
                : 'The analyst returned no report.'}
            </span>
          </div>
        ) : (
          <>
            {result.confidence !== undefined && (
              <div className="text-xs font-sans font-medium text-tertiary tabular-nums mb-2">
                confidence {Math.round(result.confidence)}%
              </div>
            )}
            {result.summary && (
              <p className="text-sm font-serif text-secondary leading-relaxed mb-2">{result.summary}</p>
            )}
            {result.recommendation && (
              <p className="text-sm font-sans text-primary leading-normal mb-2">
                <span className="font-semibold">Recommendation:</span> {result.recommendation}
              </p>
            )}
            {result.concerns && result.concerns.length > 0 && (
              <ul className="space-y-1">
                {result.concerns.map((c, i) => (
                  <li key={i} className="text-xs font-sans text-secondary leading-normal">
                    <span className="font-semibold text-tertiary">Concern:</span> {c}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

function AnalystPanel({ offeringIds }: { offeringIds: string[] }) {
  const submit = useSubmitAnalysis(offeringIds);
  const runQuery = useAnalysisRun(submit.data?.runId);
  // The polled read supersedes the 202 body once it arrives.
  const run = runQuery.data ?? submit.data;
  const inProgress = submit.isPending || (run !== undefined && !isRunSettled(run) && !runQuery.isError);
  const failure = submit.error ?? runQuery.error;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-sans font-semibold text-primary">AI analyst review</h2>
          <p className="text-sm font-sans text-secondary leading-normal">
            Three analyst personas review the selected deals.
            Decision support only -- not credit, compliance, or legal advice.
          </p>
        </div>
        <Button
          variant="secondary"
          icon={Bot}
          onClick={() => submit.mutate()}
          disabled={inProgress}
        >
          {inProgress ? 'Analyzing...' : run ? 'Re-run analysis' : 'Run analysis'}
        </Button>
      </div>

      {inProgress && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      )}

      {failure && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            {failure instanceof Error ? failure.message : 'The analysis could not be completed'}
          </div>
        </div>
      )}

      {run?.status === 'FAILED' && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">The analysis run ended without a report.</div>
        </div>
      )}

      {run?.status === 'COMPLETED' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {run.analyses.map((result) => (
            <AnalystResultCard key={result.persona} result={result} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CompareDeals() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const offeringIds = useMemo(
    () => Array.from(new Set((searchParams.get('ids') ?? '').split(',').filter(Boolean))),
    [searchParams],
  );

  const { data, isLoading, error } = useComparison(offeringIds);
  const offerings = data?.offerings ?? [];
  const idCountValid = offeringIds.length >= 2 && offeringIds.length <= 4;

  return (
    <div>
      {/* The kit BackLink, arrow first, top left, the back link every detail
          page uses. It rendered as a tertiary Button with the arrow after the
          label, and it is left out while no comparison is loaded because the
          empty state below carries the same navigation (r3 fi-g9-9,
          fi-cons-5). */}
      {idCountValid && (
        <BackLink href="/syndication/marketplace" LinkComponent={RouterLinkComponent} className="mb-4">
          Back to marketplace
        </BackLink>
      )}
      {/* Page title lives in the sticky topnav (AppHeader); the body opens
          with the descriptive subtitle, no redundant H1 -- the pattern the
          rest of the Loan Participations group uses (fi-g9-9). */}
      <p className="mb-6 text-sm font-sans text-tertiary">
        Side-by-side deal terms for the selected marketplace listings,
        assessed against your institution's regulatory profile.
      </p>

      {!idCountValid ? (
        <Card>
          <EmptyState
            icon={<Inbox className="h-8 w-8" />}
            title="Select 2-4 deals to compare"
            description="Use the checkboxes on the marketplace list, then choose Compare deals."
            action={
              <Button variant="secondary" onClick={() => navigate('/syndication/marketplace')}>
                Go to marketplace
              </Button>
            }
          />
        </Card>
      ) : error ? (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            Failed to load the comparison: {error instanceof Error ? error.message : 'unknown error'}
          </div>
        </div>
      ) : isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <>
          <ComparisonMatrix offerings={offerings} />
          <div className="mt-4 flex flex-wrap gap-2">
            {offerings.map((o) => (
              <Button
                key={o.offeringId}
                variant="tertiary"
                size="sm"
                onClick={() => navigate(`/syndication/offerings/${o.offeringId}`)}
              >
                View {o.dealName ?? formatLeadDisplay(o)}
              </Button>
            ))}
          </div>
          <AnalystPanel offeringIds={offeringIds} />
        </>
      )}
    </div>
  );
}
